import {
	ChangeEvent,
	FormEvent,
	useEffect,
	useState,
} from 'react';

import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NotificationToast } from '../../components/organisms/notification-toast';

import SVGIcon from '../../components/atoms/icons';
import Cookies from '../../lib/cookies';
import CabecalhoPainel from './components/CabecalhoPainel';
import FormularioAberturaOrdemServico, {
	problemIcons,
} from './components/FormularioAberturaOrdemServico';
import FormularioResolucaoOrdemServico from './components/FormularioResolucaoOrdemServico';
import TelaLoginFluxoUnico from './components/TelaLoginFluxoUnico';

import FluxoUnicoService from '../../services/FluxoUnico.service';
import type {
	FormularioLoginFluxoUnico,
	FormularioOrdemServicoFluxoUnico,
	FormularioResolucaoOrdemServico as TipoFormularioResolucaoOrdemServico,
	SessaoFluxoUnico,
	UsuarioFluxoUnico,
} from './types';

import type {
	IProblemaContainerFluxoUnico,
	IOrdemServicoFluxoUnico,
	TipoProblemaFluxoUnico,
} from '../../services/FluxoUnico.service';

const SESSAO_FLUXO_UNICO = 'sessao_fluxo_unico';
const USUARIO_FLUXO_UNICO = 'usuario_fluxo_unico';

const FluxoUnico = () => {
	const { i18n, t } = useTranslation();
	const [parametrosBusca] = useSearchParams();
	const [formularioLoginFluxoUnico, setFormularioLoginFluxoUnico] =
		useState<FormularioLoginFluxoUnico>({
			email: '',
			senha: '',
		});

	const [sessaoFluxoUnico, setSessaoFluxoUnico] = useState<SessaoFluxoUnico>();
	const [usuarioFluxoUnico, setUsuarioFluxoUnico] = useState<UsuarioFluxoUnico>();
	const [problemaContainer, setProblemaContainer] = useState<IProblemaContainerFluxoUnico>();

	const [carregandoProblemaContainer, setCarregandoProblemaContainer] = useState(false);
	const [carregandoLogin, setCarregandoLogin] = useState(false);
	const [carregandoCriacaoOrdemServico, setCarregandoCriacaoOrdemServico] = useState(false);
	const [carregandoResolucaoOrdemServico, setCarregandoResolucaoOrdemServico] = useState(false);

	const [erroLogin, setErroLogin] = useState('');
	const [erroCriacaoOrdemServico, setErroCriacaoOrdemServico] = useState('');
	const [erroResolucaoOrdemServico, setErroResolucaoOrdemServico] = useState('');
	const [exibindoFormularioOrdemServico, setExibindoFormularioOrdemServico] = useState(false);
	const [formularioOrdemServico, setFormularioOrdemServico] =
		useState<FormularioOrdemServicoFluxoUnico>({
			categoriaProblema: [],
			retificacao: false,
			idOrdemServicoRetificada: '',
			dataDeteccao: '',
			setPoint: '',
			temperatura: '',
			serial: '',
			tamanho: '',
			modelo: '',
			gas: '',
			quantidade: '',
			dataFabricacao: '',
			descricaoDano: '',
		});

	const [ordemServicoSelecionada, setOrdemServicoSelecionada] = useState<IOrdemServicoFluxoUnico>();
	const [formularioResolucao, setFormularioResolucao] =
		useState<TipoFormularioResolucaoOrdemServico>({
			descricaoServico: '',
			local: '',
			setPointAposServico: '',
			temperaturaAposServico: '',
			solicitadoPor: '',
			dataSolicitacao: '',
			inicioReparo: '',
			fimReparo: '',
			pecasTrocadas: '',
			tempoExecucaoHoras: '',
			tempoExecucaoMinutos: '',
			observacaoServico: '',
			imagensAposServico: [],
			nomeCompletoTecnico: '',
			assinaturaTecnico: '',
			nomeCompletoGestor: '',
			assinaturaGestor: '',
		});

	const containerParametro = parametrosBusca.get('container');
	const container = containerParametro || sessaoFluxoUnico?.container || '';
	const alertaMonitoramento = {
		assunto: '⚠️ v2 - ReferSys Monitoring Alert | Alerta de Monitoramento ⚠️',
		de: 'no-reply@reefersys.com',
		para: 'joao.silva@empresa.com.br; gestor@empresa.com.br',
		data: '14/09/2025 11:56',
	};

	useEffect(() => {
		const sessaoArmazenada = localStorage.getItem(SESSAO_FLUXO_UNICO);
		const usuarioArmazenado = localStorage.getItem(USUARIO_FLUXO_UNICO);
		if (!sessaoArmazenada) return;

		try {
			const sessao: SessaoFluxoUnico = JSON.parse(sessaoArmazenada);
			const usuario: UsuarioFluxoUnico | undefined = usuarioArmazenado
				? JSON.parse(usuarioArmazenado)
				: undefined;

			if (!sessao.idUsuario || !usuario) {
				localStorage.removeItem(SESSAO_FLUXO_UNICO);
				localStorage.removeItem(USUARIO_FLUXO_UNICO);
				return;
			}

			const containerAtual = containerParametro || sessao.container;
			const sessaoAtualizada = {
				...sessao,
				container: containerAtual,
			};

			setSessaoFluxoUnico(sessaoAtualizada);
			setUsuarioFluxoUnico(usuario);
			localStorage.setItem(
				SESSAO_FLUXO_UNICO,
				JSON.stringify(sessaoAtualizada)
			);
		} catch (error) {
			localStorage.removeItem(SESSAO_FLUXO_UNICO);
			localStorage.removeItem(USUARIO_FLUXO_UNICO);
		}
	}, [containerParametro]);

	useEffect(() => {
		const buscarProblemaContainer = async () => {
			if (!container) {
				setProblemaContainer(undefined);
				return;
			}

			setCarregandoProblemaContainer(true);
			const resposta =
				await FluxoUnicoService.getProblemaContainer(container);
			setProblemaContainer(resposta);
			setCarregandoProblemaContainer(false);
		};

		buscarProblemaContainer();
	}, [container, sessaoFluxoUnico?.idUsuario]);

	const lidarComInput = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		setFormularioLoginFluxoUnico((formularioAnterior) => ({
			...formularioAnterior,
			[name]: value,
		}));
	};

	const lidarComLogout = () => {
		Cookies.clear();
		localStorage.removeItem(SESSAO_FLUXO_UNICO);
		localStorage.removeItem(USUARIO_FLUXO_UNICO);
		setSessaoFluxoUnico(undefined);
		setUsuarioFluxoUnico(undefined);
		setProblemaContainer(undefined);
		setExibindoFormularioOrdemServico(false);
		setOrdemServicoSelecionada(undefined);
	};

	const lidarComSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErroLogin('');

		if (
			!formularioLoginFluxoUnico.email ||
			!formularioLoginFluxoUnico.senha
		) {
			setErroLogin(t('fluxo_unico.messages.fill_email_password'));
			return;
		}

		setCarregandoLogin(true);
		const resposta = await FluxoUnicoService.login(
			formularioLoginFluxoUnico.email,
			formularioLoginFluxoUnico.senha
		);
		setCarregandoLogin(false);

		if (!resposta) {
			setErroLogin(t('fluxo_unico.messages.invalid_credentials'));
			return;
		}

		const novaSessao = {
			idUsuario: resposta.user.id,
			email: resposta.user.email,
			nome: resposta.user.name,
			perfil: resposta.user.role,
			container,
		};

		setSessaoFluxoUnico(novaSessao);
		setUsuarioFluxoUnico(resposta.user);

		localStorage.setItem(SESSAO_FLUXO_UNICO, JSON.stringify(novaSessao));
		localStorage.setItem(USUARIO_FLUXO_UNICO, JSON.stringify(resposta.user));
	};

	const formatarTemperatura = (temperatura?: number | null) => {
		if (temperatura === null || temperatura === undefined) return '-';
		return `${temperatura}°C`;
	};

	const formatarDataHora = (data?: string | null) => {
		if (!data) return '-';

		return new Intl.DateTimeFormat(i18n.language, {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'America/Manaus',
		}).format(new Date(data));
	};

	const calcularTempoPatio = (
		dataEntradaContainer?: string | null,
		dataSaidaContainer?: string | null
	) => {
		if (!dataEntradaContainer) return undefined;

		const dataInicial = new Date(dataEntradaContainer);
		const dataFinal = dataSaidaContainer
			? new Date(dataSaidaContainer)
			: new Date();

		if (Number.isNaN(dataInicial.getTime()) || Number.isNaN(dataFinal.getTime())) {
			return undefined;
		}

		const umDiaEmMilissegundos = 1000 * 60 * 60 * 24;
		const diferencaEmDias = Math.ceil(
			(dataFinal.getTime() - dataInicial.getTime()) /
			umDiaEmMilissegundos
		);

		return Math.max(diferencaEmDias, 0);
	};

	const formatarStatusOrdemServico = (status: IOrdemServicoFluxoUnico['status']) => {
		if (status === 'fechada') return t('fluxo_unico.status.repaired_ok');
		if (status === 'rascunho') return t('fluxo_unico.status.draft');
		return t('fluxo_unico.status.in_service');
	};

	const formatarTipoProblema = (tipoProblema: TipoProblemaFluxoUnico | number[]) => {
		if (Array.isArray(tipoProblema)) return '-';

		const tiposProblema = {
			critico: t('fluxo_unico.problem_type.critical'),
			severo: t('fluxo_unico.problem_type.severe'),
			alto: t('fluxo_unico.problem_type.high'),
			medio: t('fluxo_unico.problem_type.medium'),
			baixo: t('fluxo_unico.problem_type.low'),
		};

		return tiposProblema[tipoProblema];
	};

	const lidarComAberturaOrdemServico = () => {
		setErroCriacaoOrdemServico('');
		const problemasDetectados = problemaContainer?.problems?.map(Number) || [];
		setFormularioOrdemServico({
			categoriaProblema: problemasDetectados.length
				? problemasDetectados
				: [4],
			retificacao: false,
			idOrdemServicoRetificada: '',
			dataDeteccao: problemaContainer?.date
				? formatarDataHora(problemaContainer.date)
				: '',
			setPoint: String(problemaContainer?.container.temperature ?? ''),
			temperatura: String(problemaContainer?.temperature ?? ''),
			serial: '',
			tamanho: '',
			modelo: '',
			gas: '',
			quantidade: '',
			dataFabricacao: '',
			descricaoDano: '',
		});
		setExibindoFormularioOrdemServico(true);
	};

	const lidarComCriacaoOrdemServico = async (
		event: FormEvent<HTMLFormElement>
	) => {
		event.preventDefault();
		setErroCriacaoOrdemServico('');

		if (!sessaoFluxoUnico || !problemaContainer) {
			setErroCriacaoOrdemServico(
				t('fluxo_unico.messages.container_monitoring_not_found')
			);
			return;
		}

		const setPointInformado = Number(formularioOrdemServico.setPoint);
		const temperaturaInformada = Number(formularioOrdemServico.temperatura);

		if (
			!Number.isFinite(setPointInformado) ||
			!Number.isFinite(temperaturaInformada)
		) {
			setErroCriacaoOrdemServico(
				t('fluxo_unico.messages.invalid_temperatures')
			);
			return;
		}

		if (!formularioOrdemServico.categoriaProblema.length) {
			setErroCriacaoOrdemServico(t('fluxo_unico.form.problem_category'));
			return;
		}

		setCarregandoCriacaoOrdemServico(true);
		const resposta = await FluxoUnicoService.criarOrdemServico({
			id_usuario: sessaoFluxoUnico.idUsuario,
			id_container: problemaContainer.container.id,
			id_monitoramento: problemaContainer.id,
			id_ordem_servico_retificada: formularioOrdemServico.retificacao
				? Number(formularioOrdemServico.idOrdemServicoRetificada)
				: undefined,
			data: problemaContainer.date,
			set_point: setPointInformado,
			temperatura: temperaturaInformada,
			serial: formularioOrdemServico.serial || undefined,
			size: formularioOrdemServico.tamanho || undefined,
			model: formularioOrdemServico.modelo || undefined,
			gas: formularioOrdemServico.gas || undefined,
			qty: formularioOrdemServico.quantidade || undefined,
			manufactore_date: formularioOrdemServico.dataFabricacao || undefined,
			damage_description: formularioOrdemServico.descricaoDano || undefined,
			imagens: problemaContainer.images.length
				? problemaContainer.images
				: undefined,
			tipo_problema: formularioOrdemServico.categoriaProblema,
			status: 'em-atendimento',
		});

		if (!resposta) {
			const mensagemErro = t('fluxo_unico.messages.create_report_error');
			setErroCriacaoOrdemServico(mensagemErro);
			NotificationToast.Warn(mensagemErro);
			setCarregandoCriacaoOrdemServico(false);
			return;
		}

		const problemaAtualizado =
			await FluxoUnicoService.getProblemaContainer(container);
		setProblemaContainer(problemaAtualizado || problemaContainer);
		setCarregandoCriacaoOrdemServico(false);
		setExibindoFormularioOrdemServico(false);
		setOrdemServicoSelecionada(undefined);
		NotificationToast.Success(t('fluxo_unico.messages.create_report_success'));
	};

	const lidarComSelecaoOrdemServico = (
		ordemServico: IOrdemServicoFluxoUnico
	) => {
		const ordemServicoComContainer = {
			...ordemServico,
			container: ordemServico.container || problemaContainer?.container,
		};

		setOrdemServicoSelecionada(ordemServicoComContainer);
		setFormularioResolucao({
			descricaoServico: ordemServico.descricaoServico || '',
			local: ordemServico.local || '',
			setPointAposServico: String(ordemServico.setPointAposServico ?? ''),
			temperaturaAposServico: String(
				ordemServico.temperaturaAposServico ?? ''
			),
			solicitadoPor: ordemServico.solicitadoPor || '',
			dataSolicitacao: ordemServico.dataSolicitacao || '',
			inicioReparo: ordemServico.inicioReparo || '',
			fimReparo: ordemServico.fimReparo || '',
			pecasTrocadas: ordemServico.pecasTrocadas || '',
			tempoExecucaoHoras: String(ordemServico.tempoExecucaoHoras ?? ''),
			tempoExecucaoMinutos: String(
				ordemServico.tempoExecucaoMinutos ?? ''
			),
			observacaoServico: ordemServico.observacaoServico || '',
			imagensAposServico: ordemServico.imagensAposServico || [],
			nomeCompletoTecnico: ordemServico.nomeCompletoTecnico || '',
			assinaturaTecnico: ordemServico.assinaturaTecnico || '',
			nomeCompletoGestor: ordemServico.nomeCompletoGestor || '',
			assinaturaGestor: ordemServico.assinaturaGestor || '',
		});
		setErroResolucaoOrdemServico('');
	};

	const lidarComCampoResolucao = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = event.target;

		setFormularioResolucao((formularioAnterior) => ({
			...formularioAnterior,
			[name]: value,
		}));
	};

	const lidarComFotoDepois = (event: ChangeEvent<HTMLInputElement>) => {
		const arquivos = Array.from(event.target.files || []);

		if (!arquivos.length) return;

		const leiturasArquivos = arquivos.map((arquivo) => {
			return new Promise<string | null>((resolve) => {
				if (!arquivo.type.startsWith('image/')) {
					NotificationToast.Warn(t('fluxo_unico.messages.image_only'));
					resolve(null);
					return;
				}

				const leitorArquivo = new FileReader();
				leitorArquivo.onload = () => resolve(leitorArquivo.result as string);
				leitorArquivo.onerror = () => resolve(null);
				leitorArquivo.readAsDataURL(arquivo);
			});
		});

		Promise.all(leiturasArquivos).then((imagensSelecionadas) => {
			const imagensValidas = imagensSelecionadas.filter(Boolean) as string[];

			setFormularioResolucao((formularioAnterior) => ({
				...formularioAnterior,
				imagensAposServico: [
					...formularioAnterior.imagensAposServico,
					...imagensValidas,
				],
			}));
		});

		event.target.value = '';
	};

	const removerFotoDepois = (indiceFoto: number) => {
		setFormularioResolucao((formularioAnterior) => ({
			...formularioAnterior,
			imagensAposServico: formularioAnterior.imagensAposServico.filter(
				(_, indice) => indice !== indiceFoto
			),
		}));
	};

	const montarPayloadResolucaoOrdemServico = (
		statusOS: 'rascunho' | 'fechada'
	) => {
		if (!sessaoFluxoUnico || !ordemServicoSelecionada) return undefined;

		const setPointAposServico = Number(
			formularioResolucao.setPointAposServico
		);
		const temperaturaAposServico = Number(
			formularioResolucao.temperaturaAposServico
		);
		const tempoExecucaoHoras = Number(formularioResolucao.tempoExecucaoHoras);
		const tempoExecucaoMinutos = Number(
			formularioResolucao.tempoExecucaoMinutos
		);

		return {
			id: ordemServicoSelecionada.id,
			data: ordemServicoSelecionada.data,
			set_point: ordemServicoSelecionada.setPoint,
			temperatura: ordemServicoSelecionada.temperatura,
			imagens: ordemServicoSelecionada.imagens || undefined,
			set_point_apos_servico: Number.isFinite(setPointAposServico)
				? setPointAposServico
				: undefined,
			temperatura_apos_servico: Number.isFinite(temperaturaAposServico)
				? temperaturaAposServico
				: undefined,
			requested_attendency: formularioResolucao.solicitadoPor || undefined,
			request_date: formularioResolucao.dataSolicitacao || undefined,
			repair_start: formularioResolucao.inicioReparo || undefined,
			repair_finish: formularioResolucao.fimReparo || undefined,
			imagens_apos_servico: formularioResolucao.imagensAposServico.length
				? formularioResolucao.imagensAposServico
				: undefined,
			descricao_servico: formularioResolucao.descricaoServico || undefined,
			local: formularioResolucao.local || undefined,
			pecas_trocadas: formularioResolucao.pecasTrocadas || undefined,
			tempo_execucao_horas: Number.isFinite(tempoExecucaoHoras)
				? tempoExecucaoHoras
				: undefined,
			tempo_execucao_minutos: Number.isFinite(tempoExecucaoMinutos)
				? tempoExecucaoMinutos
				: undefined,
			observacao_servico: formularioResolucao.observacaoServico || undefined,
			nome_completo_tecnico: formularioResolucao.nomeCompletoTecnico || undefined,
			assinatura_tecnico: formularioResolucao.assinaturaTecnico || undefined,
			nome_completo_gestor: formularioResolucao.nomeCompletoGestor || undefined,
			assinatura_gestor: formularioResolucao.assinaturaGestor || undefined,
			tipo_problema: ordemServicoSelecionada.tipoProblema,
			status: statusOS,
		};
	};

	const validarCamposObrigatoriosResolucao = () => {
		const camposObrigatorios = [
			{
				nome: t('fluxo_unico.resolution.after_photo_required'),
				preenchido: formularioResolucao.imagensAposServico.length > 0,
			},
			{
				nome: t('fluxo_unico.resolution.description_done'),
				preenchido: Boolean(formularioResolucao.descricaoServico.trim()),
			},
			{
				nome: t('fluxo_unico.resolution.temperature_after'),
				preenchido: Boolean(formularioResolucao.temperaturaAposServico),
			},
			{
				nome: t('fluxo_unico.resolution.execution_time_minutes'),
				preenchido: Boolean(formularioResolucao.tempoExecucaoMinutos),
			},
			{
				nome: t('fluxo_unico.resolution.technician_full_name'),
				preenchido: Boolean(formularioResolucao.nomeCompletoTecnico.trim()),
			},
			{
				nome: t('fluxo_unico.resolution.technician_signature'),
				preenchido: Boolean(formularioResolucao.assinaturaTecnico),
			},
		];

		const campoNaoPreenchido = camposObrigatorios.find(
			(campo) => !campo.preenchido
		);

		if (!campoNaoPreenchido) return true;

		const mensagemErro = t('fluxo_unico.messages.required_field', {
			field: campoNaoPreenchido.nome,
		});
		setErroResolucaoOrdemServico(mensagemErro);
		NotificationToast.Warn(mensagemErro);
		return false;
	};

	const lidarComUpsertResolucaoOrdemServico = async (
		statusOS: 'rascunho' | 'fechada'
	) => {
		setErroResolucaoOrdemServico('');

		if (!validarCamposObrigatoriosResolucao()) return;

		const payloadResolucao = montarPayloadResolucaoOrdemServico(statusOS);

		if (!payloadResolucao) {
			const mensagemErro = t('fluxo_unico.messages.report_not_found');
			setErroResolucaoOrdemServico(mensagemErro);
			NotificationToast.Warn(mensagemErro);
			return;
		}

		setCarregandoResolucaoOrdemServico(true);
		const resposta = await FluxoUnicoService.criarOrdemServico(payloadResolucao);

		if (!resposta) {
			const mensagemErro = t('fluxo_unico.messages.save_resolution_error');
			setErroResolucaoOrdemServico(mensagemErro);
			NotificationToast.Warn(mensagemErro);

			setCarregandoResolucaoOrdemServico(false);
			return;
		}

		const problemaAtualizado =
			await FluxoUnicoService.getProblemaContainer(container);

		setProblemaContainer(problemaAtualizado || problemaContainer);
		setOrdemServicoSelecionada({
			...resposta,
			container: resposta.container || problemaContainer?.container,
		});
		setCarregandoResolucaoOrdemServico(false);

		if (statusOS === 'rascunho') {
			NotificationToast.Success(t('fluxo_unico.messages.draft_saved'));
			return;
		}

		NotificationToast.Success(t('fluxo_unico.messages.report_closed_sent'));
		setOrdemServicoSelecionada(undefined);
	};

	const voltarAoPainel = () => {
		setExibindoFormularioOrdemServico(false);
		setOrdemServicoSelecionada(undefined);
	};

	const lidarComCampoOrdemServico = (
		event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
	) => {
		const { name, value } = event.target;
		const valor = name === 'retificacao'
			? (event.target as HTMLInputElement).checked
			: value;

		setFormularioOrdemServico((formularioAnterior) => ({
			...formularioAnterior,
			[name]: valor,
			...(name === 'retificacao' && !valor
				? { idOrdemServicoRetificada: '' }
				: {}),
		}));
	};

	const lidarComCategoriaProblema = (idProblema: number) => {
		setFormularioOrdemServico((formularioAnterior) => {
			const categoriaProblema = formularioAnterior.categoriaProblema.includes(idProblema)
				? formularioAnterior.categoriaProblema.filter((idAtual) => idAtual !== idProblema)
				: [...formularioAnterior.categoriaProblema, idProblema];

			return {
				...formularioAnterior,
				categoriaProblema,
			};
		});
	};

	const setPoint = problemaContainer?.container.temperature ?? 0;
	const temperaturaAtual = problemaContainer?.temperature ?? 0;

	const variacao =
		temperaturaAtual !== null && setPoint !== null
			? temperaturaAtual - setPoint
			: null;

	const retorno = problemaContainer?.container.return ?? 0;
	const returnoAtual = problemaContainer?.return ?? 0;

	const variacaoRetorno =
		returnoAtual !== null && retorno !== null
			? returnoAtual - retorno
			: null;

	const supply = problemaContainer?.container.supply ?? 0;
	const supplyAtual = problemaContainer?.supply ?? 0;

	const variacaoSupply =
		supplyAtual !== null && supply !== null
			? supplyAtual - supply
			: null;

	const containerPainel = problemaContainer?.container.label || container || '-';
	const containerSelecionado = problemaContainer?.container;
	const clientePainel = problemaContainer?.container.owner || '-';
	const localizacaoPainel =
		problemaContainer?.container?.movement!.yard.name
		|| '-';

	const posicaoPainel =
		problemaContainer?.container?.position
		|| '-';

	const modeloPainel = 'Reefer';
	const dataDeteccao = problemaContainer?.date
		? formatarDataHora(problemaContainer.date)
		: alertaMonitoramento.data;

	const emailDetalhes = problemaContainer?.email_detalhes;
	const tituloEmail = emailDetalhes?.titulo || alertaMonitoramento.assunto;
	const remetenteEmail = emailDetalhes?.remetente || alertaMonitoramento.de;
	const destinatariosEmail = emailDetalhes?.destinatarios || alertaMonitoramento.para;

	const dataEntrada = problemaContainer?.container.dateArrive
		? formatarDataHora(problemaContainer.container.dateArrive)
		: '-';

	const tempoPatio = calcularTempoPatio(
		problemaContainer?.container.dateArrive,
		problemaContainer?.container.dateLeave
	);

	const ordensServico = problemaContainer?.['ordens-servico'] || [];
	const quantidadeOrdensResolvidas = ordensServico.filter(
		(ordemServico) => ordemServico.status === 'fechada'
	).length;

	const quantidadeOrdensAbertas = ordensServico.length - quantidadeOrdensResolvidas;
	const supplyReturnAtivado =
		usuarioFluxoUnico?.client.system_configs.supplyReturnAtivado === true;

	const setPointFormulario = Number(formularioOrdemServico.setPoint);
	const temperaturaFormulario = Number(formularioOrdemServico.temperatura);
	const diferencaTemperaturaFormulario =
		formularioOrdemServico.setPoint !== '' &&
			formularioOrdemServico.temperatura !== '' &&
			Number.isFinite(setPointFormulario) &&
			Number.isFinite(temperaturaFormulario)
			? Math.abs(temperaturaFormulario - setPointFormulario)
			: undefined;

	const classeAlertaTemperatura =
		diferencaTemperaturaFormulario !== undefined &&
			diferencaTemperaturaFormulario >= 4
			? 'border-[#ef4444] bg-[#fff1f2] text-[#dc2626] focus:border-[#ef4444]'
			: diferencaTemperaturaFormulario !== undefined &&
				diferencaTemperaturaFormulario >= 3
				? 'border-[#f59e0b] bg-[#fffbeb] text-[#b45309] focus:border-[#f59e0b]'
				: 'border-[#cbd5e1] bg-white text-[#1f2937] focus:border-[#2f70f0]';

	if (
		sessaoFluxoUnico &&
		ordemServicoSelecionada?.status === 'fechada'
	) {
		const containerOrdemServico =
			ordemServicoSelecionada.container || containerSelecionado;
		const descricaoResolucao =
			ordemServicoSelecionada.descricaoServico ||
			ordemServicoSelecionada.observacaoServico ||
			t('fluxo_unico.closed.default_resolution');
		const ordemServicoDados = ordemServicoSelecionada as IOrdemServicoFluxoUnico & Record<string, any>;
		const obterCampoOrdemServico = (...campos: string[]) => {
			for (const campo of campos) {
				const valor = ordemServicoDados[campo];
				if (valor !== null && valor !== undefined && valor !== '') return valor;
			}
			return undefined;
		};
		const formatarDataResumo = (data?: string | null) =>
			data ? formatarDataHora(data) : undefined;
		const tiposProblemaNumericos = Array.isArray(ordemServicoSelecionada.tipoProblema)
			? (ordemServicoSelecionada.tipoProblema as number[])
			: [];
		const problemasSelecionados = problemIcons.filter((problema) =>
			tiposProblemaNumericos.includes(problema.id)
		);
		const obterProcessosFluxoUnico = () => {
			if (!problemasSelecionados.length) {
				return Array.isArray(ordemServicoSelecionada.tipoProblema)
					? '-'
					: formatarTipoProblema(ordemServicoSelecionada.tipoProblema);
			}

			return (
				<div className="flex flex-wrap items-center justify-center gap-2">
					{problemasSelecionados.map((problema) => (
						<span
							aria-label={problema.label}
							className="flex size-10 items-center justify-center rounded-md border border-[#222]"
							key={problema.id}
							title={problema.label}
							style={{ backgroundColor: problema.backgroundColor }}
						>
							<SVGIcon
								className="size-7"
								icon={problema.iconeSvg}
								style={{
									fill: problema.iconColor,
									color: problema.iconColor,
									stroke: problema.iconColor,
								}}
							/>
						</span>
					))}
				</div>
			);
		};
		const renderizarValorTabela = (valor?: string | number | null) => {
			if (valor === null || valor === undefined || valor === '') return null;

			return valor;
		};
		const renderizarTabelaResumo = (
			titulo: string,
			linhas: { rotulo: string; valor?: string | number | null }[]
		) => {
			const linhasDisponiveis = linhas.filter((linha) =>
				renderizarValorTabela(linha.valor) !== null
			);
			if (!linhasDisponiveis.length) return null;

			return (
				<div className="mt-8">
					<h2 className="mb-3 text-center text-lg font-bold text-[#2b2b2b]">
						{titulo}
					</h2>
					<div className="overflow-x-auto">
						<table className="w-full min-w-[720px] border-collapse text-sm text-[#2b2b2b]">
							<tbody>
								{linhasDisponiveis.map((linha) => (
									<tr key={linha.rotulo}>
										<th className="w-56 border border-[#222] bg-[#eef2f7] px-3 py-2 text-left font-bold">
											{linha.rotulo}
										</th>
										<td className="border border-[#222] px-3 py-2 text-left">
											{linha.valor}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			);
		};
		const imagensAntes = ordemServicoSelecionada.imagens || [];
		const imagensDepois = ordemServicoSelecionada.imagensAposServico || [];
		const idOrdemServicoRetificada = obterCampoOrdemServico(
			'idOrdemServicoRetificada',
			'id_ordem_servico_retificada'
		);
		const iconesProblemasResumo = [
			{
				nome: 'Baixo Rendimento',
				icone: 'https://s3.sa-east-1.amazonaws.com/report-assets.reefersys.com/warning_amber.png',
			},
			{
				nome: 'Temperatura Crítica',
				icone: 'https://s3.sa-east-1.amazonaws.com/report-assets.reefersys.com/highlight_off.png',
			},
			{
				nome: 'Problema Crítico',
				icone: 'https://s3.sa-east-1.amazonaws.com/report-assets.reefersys.com/notification_important_outlined.png',
			},
			{
				nome: 'Sem Alimentação',
				icone: 'https://s3.sa-east-1.amazonaws.com/report-assets.reefersys.com/power_off_outlined.png',
			},
			{
				nome: 'Falta de Energia no Pátio',
				icone: 'https://s3.sa-east-1.amazonaws.com/report-assets.reefersys.com/outlet_outlined.png',
			},
			{
				nome: 'Manutenção Preventiva',
				icone: 'https://s3.sa-east-1.amazonaws.com/report-assets.reefersys.com/preventive.png',
			},
		];
		return (
			<main className="min-h-full bg-[#f5f7fa] text-[#1f2937]">
				{sessaoFluxoUnico && (
					<CabecalhoPainel
						aoSair={lidarComLogout}
						sessaoFluxoUnico={sessaoFluxoUnico}
					/>
				)}

				<section className="mx-auto max-w-7xl px-6 py-10">
					<p className="mb-7 font-mono text-xs text-[#64748b]">
						{t('fluxo_unico.breadcrumb.email')} › {t('fluxo_unico.breadcrumb.record')} ›{' '}
						<span className="text-[#2f70f0]">
							{t('fluxo_unico.breadcrumb.rat', { id: ordemServicoSelecionada.id })}
						</span>
					</p>

					<div className="mb-8 flex items-center gap-4">
						<button
							aria-label={t('fluxo_unico.closed.back_record')}
							className="flex size-8 items-center justify-center rounded-md border border-[#cbd5e1] bg-white transition-colors hover:border-[#2f70f0] hover:bg-[#f8fafc] focus:outline-none focus:ring-2 focus:ring-[#bfdbfe]"
							onClick={voltarAoPainel}
							title={t('fluxo_unico.closed.back_record')}
							type="button"
						>
							<SVGIcon
								className="h-2 w-3 fill-[#475569]"
								direction="left"
								icon="Arrow"
							/>
						</button>
						<h1 className="text-3xl font-bold tracking-normal text-[#1f2937]">
							RAT resolvido #{ordemServicoSelecionada.id} ✅
						</h1>
					</div>

					<div className="bg-white px-6 py-8 text-[#2b2b2b] shadow-2xl sm:px-10">
						<div className="text-center">
							<a
								href="https://web-v2.reefersys.com/"
								rel="noreferrer"
								target="_blank"
							>
								<img
									alt="Inova Reefer"
									className="mx-auto h-auto w-[150px]"
									src="https://reefersys-v2-assets.s3.us-east-1.amazonaws.com/logo-inova-reefer.png"
								/>
							</a>
							<h1 className="mt-5 text-2xl font-bold uppercase tracking-normal">
								Resumo de Resolução do RAT
							</h1>
							<p className="mt-2 text-sm">
								Relatório gerado pelo Container Tracking para registro da tratativa técnica do container{' '}
								<strong>{containerOrdemServico?.label || containerPainel} - {containerOrdemServico.carrier}</strong>.
							</p>
						</div>

						<div className="mx-auto mt-8 grid max-w-5xl grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
							{iconesProblemasResumo.map((problema) => (
								<div
									className="text-center"
									key={problema.nome}
								>
									<img
										alt={problema.nome}
										className="mx-auto block h-[48px] w-[60px]"
										src={problema.icone}
									/>
									<h2 className="mt-3 text-sm font-bold">
										{problema.nome}
									</h2>
								</div>
							))}
						</div>

						<div className="mt-10 text-center">
							<h2 className="text-xl font-bold">
								Resultado do Container Tracking
							</h2>
							<p className="mt-5 text-sm">
								Este resumo consolida abertura, atendimento e resolução do RAT{' '}
								<strong>#{ordemServicoSelecionada.id}</strong>.
							</p>
						</div>

						<div className="mx-auto mt-8 max-w-5xl space-y-3 text-left text-base">
							<p>
								<strong className="text-[#FF0000]">DEFROST</strong>
								{' | '}Procedimento padrão do contêiner para remoção do acúmulo de gelo.
							</p>
							<p>
								<strong className="text-[#00BCDE]">COOLINGDOWN</strong>
								{' | '}Processo normal de resfriamento utilizado para restaurar a temperatura adequada da carga.
							</p>
						</div>

						<div className="mt-4 overflow-x-auto">
							<table className="w-full min-w-[920px] border-collapse text-center text-sm text-[#2b2b2b]">
								<thead>
									<tr>
										<th className="border border-[#222] bg-[#eef2f7] px-3 py-2">Container</th>
										<th className="border border-[#222] bg-[#eef2f7] px-3 py-2">SetPoint</th>
										<th className="border border-[#222] bg-[#eef2f7] px-3 py-2">Temperatura</th>
										<th className="border border-[#222] bg-[#eef2f7] px-3 py-2">Processo</th>
										<th className="border border-[#222] bg-[#eef2f7] px-3 py-2">Status</th>
										<th className="border border-[#222] bg-[#eef2f7] px-3 py-2">Fotos</th>
										<th className="border border-[#222] bg-[#eef2f7] px-3 py-2">Posição</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-[#222] px-3 py-3">
											{containerOrdemServico?.label || containerPainel} - {containerOrdemServico.carrier}
										</td>
										<td className="border border-[#222] px-3 py-3">
											{formatarTemperatura(ordemServicoSelecionada.setPoint)}
										</td>
										<td className="border border-[#222] px-3 py-3">
											{formatarTemperatura(ordemServicoSelecionada.temperatura)}
										</td>
										<td className="border border-[#222] px-3 py-3">
											{obterProcessosFluxoUnico()}
										</td>
										<td className="border border-[#222] px-3 py-3">
											{formatarStatusOrdemServico(ordemServicoSelecionada.status)}
										</td>
										<td className="border border-[#222] px-3 py-3">
											{imagensAntes.length + imagensDepois.length || 'Sem fotos'}
										</td>
										<td className="border border-[#222] px-3 py-3">
											{containerOrdemServico?.position || posicaoPainel}
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						{renderizarTabelaResumo('Identificação', [
							{ rotulo: 'RAT', valor: `#${ordemServicoSelecionada.id}` },
							{ rotulo: 'Retificação do RAT', valor: idOrdemServicoRetificada ? `RAT - #${idOrdemServicoRetificada}` : undefined },
							{ rotulo: 'Cliente', valor: containerOrdemServico?.owner || clientePainel },
							{ rotulo: 'Localização', valor: containerOrdemServico?.movement?.yard?.name || localizacaoPainel },
							{ rotulo: 'Modelo', valor: modeloPainel },
							{ rotulo: 'Aberto em', valor: formatarDataResumo(ordemServicoSelecionada.data) },
							{ rotulo: 'Resolvido em', valor: formatarDataResumo(ordemServicoSelecionada.updatedAt || ordemServicoSelecionada.data) },
						])}

						{renderizarTabelaResumo('Dados do Equipamento', [
							{ rotulo: 'Serial', valor: obterCampoOrdemServico('serial') },
							{ rotulo: 'Size', valor: obterCampoOrdemServico('size') },
							{ rotulo: 'Model', valor: obterCampoOrdemServico('model') },
							{ rotulo: 'Gas', valor: obterCampoOrdemServico('gas') },
							{ rotulo: 'Qty', valor: obterCampoOrdemServico('qty') },
							{ rotulo: 'Manufacture Date', valor: formatarDataResumo(obterCampoOrdemServico('manufactoreDate', 'manufactore_date')) },
							{ rotulo: 'Damage Description', valor: obterCampoOrdemServico('damageDescription', 'damage_description') },
							{ rotulo: 'Local', valor: ordemServicoSelecionada.local },
						])}

						{renderizarTabelaResumo('Atendimento Técnico', [
							{ rotulo: 'Solicitado por', valor: obterCampoOrdemServico('requestedAttendency', 'requested_attendency', 'solicitadoPor') },
							{ rotulo: 'Data de solicitação', valor: formatarDataResumo(obterCampoOrdemServico('requestDate', 'request_date', 'dataSolicitacao')) },
							{ rotulo: 'Início do reparo', valor: formatarDataResumo(obterCampoOrdemServico('repairStart', 'repair_start', 'inicioReparo')) },
							{ rotulo: 'Fim do reparo', valor: formatarDataResumo(obterCampoOrdemServico('repairFinish', 'repair_finish', 'fimReparo')) },
							{ rotulo: 'Tempo de execução', valor: `${ordemServicoSelecionada.tempoExecucaoHoras ?? 0}h ${ordemServicoSelecionada.tempoExecucaoMinutos ?? 0}min` },
							{ rotulo: 'Técnico', valor: ordemServicoSelecionada.nomeCompletoTecnico || ordemServicoSelecionada.usuario?.name || sessaoFluxoUnico.nome },
							{ rotulo: 'Gestor', valor: ordemServicoSelecionada.nomeCompletoGestor },
						])}

						{renderizarTabelaResumo('Resolução', [
							{ rotulo: 'Descrição do serviço', valor: descricaoResolucao },
							{ rotulo: 'Peças trocadas', valor: ordemServicoSelecionada.pecasTrocadas },
							{ rotulo: 'Observações', valor: ordemServicoSelecionada.observacaoServico },
							{ rotulo: 'SetPoint após serviço', valor: formatarTemperatura(ordemServicoSelecionada.setPointAposServico) },
							{ rotulo: 'Temperatura após serviço', valor: formatarTemperatura(ordemServicoSelecionada.temperaturaAposServico) },
						])}

						{(imagensAntes.length > 0 || imagensDepois.length > 0) && (
							<div className="mt-8">
								<h2 className="mb-3 text-center text-lg font-bold text-[#2b2b2b]">
									Evidências
								</h2>
								<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
									{[...imagensAntes, ...imagensDepois].map((imagem, indice) => (
										<a
											className="block overflow-hidden border border-[#222]"
											href={imagem}
											key={`${imagem}-${indice}`}
											rel="noreferrer"
											target="_blank"
										>
											<img
												alt={`Evidência ${indice + 1}`}
												className="h-40 w-full object-cover"
												src={imagem}
											/>
										</a>
									))}
								</div>
							</div>
						)}

						<p className="mt-8 text-center text-sm">
							<strong className="text-red-600">ATENÇÃO:</strong> o endereço usado para enviar este alerta não é monitorado.
							<br />
							Em caso de dúvidas contatar o gestor ou o administrativo do pátio.
						</p>

						<div className="mt-6 text-center">
							<img
								alt="ReeferSys"
								className="mx-auto h-[60px] w-[250px] object-contain"
								src="https://reefersys-v2-assets.s3.us-east-1.amazonaws.com/reefer-logo-rgb.png"
							/>
						</div>
					</div>

					<div className="mt-8 flex flex-wrap justify-start gap-4 border-t border-[#e2e8f0] pt-7">
						<button
							className="rounded-md border border-[#2f70f0] bg-[#2f70f0] px-5 py-3 font-semibold text-white transition-colors hover:bg-[#245dcc]"
							onClick={() => window.print()}
							type="button"
						>
							{t('fluxo_unico.closed.print')}
						</button>
						<button
							className="rounded-md border border-[#cbd5e1] bg-white px-5 py-3 font-semibold text-[#475569] transition-colors hover:border-[#94a3b8] hover:bg-[#f8fafc]"
							onClick={voltarAoPainel}
							type="button"
						>
							{t('fluxo_unico.closed.back_record')}
						</button>
					</div>
				</section>
			</main>
		);
	}

	if (sessaoFluxoUnico && ordemServicoSelecionada) {
		return (
			<main className="min-h-full bg-[#f5f7fa] text-[#1f2937]">
				{sessaoFluxoUnico && (
					<CabecalhoPainel
						aoSair={lidarComLogout}
						sessaoFluxoUnico={sessaoFluxoUnico}
					/>
				)}

				<FormularioResolucaoOrdemServico
					aoAlterarAssinaturaGestor={(assinaturaGestor) =>
						setFormularioResolucao((formularioAnterior) => ({
							...formularioAnterior,
							assinaturaGestor,
						}))
					}
					aoAlterarAssinaturaTecnico={(assinaturaTecnico) =>
						setFormularioResolucao((formularioAnterior) => ({
							...formularioAnterior,
							assinaturaTecnico,
						}))
					}
					aoAlterarCampo={lidarComCampoResolucao}
					aoAlterarFotoDepois={lidarComFotoDepois}
					aoRemoverFotoDepois={removerFotoDepois}
					aoSalvarResolucao={lidarComUpsertResolucaoOrdemServico}
					aoVoltar={voltarAoPainel}
					carregandoResolucaoOrdemServico={carregandoResolucaoOrdemServico}
					containerPainel={containerPainel}
					containerSelecionado={containerSelecionado}
					erroResolucaoOrdemServico={erroResolucaoOrdemServico}
					formatarDataHora={formatarDataHora}
					formatarStatusOrdemServico={formatarStatusOrdemServico}
					formatarTemperatura={formatarTemperatura}
					formatarTipoProblema={formatarTipoProblema}
					formularioResolucao={formularioResolucao}
					ordemServicoSelecionada={ordemServicoSelecionada}
				/>
			</main>
		);
	}

	if (sessaoFluxoUnico && exibindoFormularioOrdemServico) {
		return (
			<main className="min-h-full bg-[#f5f7fa] text-[#1f2937]">
				{sessaoFluxoUnico && (
					<CabecalhoPainel
						aoSair={lidarComLogout}
						sessaoFluxoUnico={sessaoFluxoUnico}
					/>
				)}

				<FormularioAberturaOrdemServico
					aoAlterarCampo={lidarComCampoOrdemServico}
					aoAlterarCategoriaProblema={lidarComCategoriaProblema}
					aoCriarOrdemServico={lidarComCriacaoOrdemServico}
					aoVoltar={() => setExibindoFormularioOrdemServico(false)}
					carregandoCriacaoOrdemServico={carregandoCriacaoOrdemServico}
					classeAlertaTemperatura={classeAlertaTemperatura}
					containerPainel={containerPainel}
					containerSelecionado={containerSelecionado}
					erroCriacaoOrdemServico={erroCriacaoOrdemServico}
					formularioOrdemServico={formularioOrdemServico}
					problemaContainer={problemaContainer}
				/>
			</main>
		);
	}

	if (sessaoFluxoUnico) {
		return (
			<main className="min-h-full bg-[#f5f7fa] text-[#1f2937]">
				{sessaoFluxoUnico && (
					<CabecalhoPainel
						aoSair={lidarComLogout}
						sessaoFluxoUnico={sessaoFluxoUnico}
					/>
				)}

				<section className="mx-auto max-w-7xl px-6 py-6">
					<p className="mb-4 font-mono text-xs text-[#64748b]">
						{t('fluxo_unico.breadcrumb.email')} › <span className="text-[#2f70f0]">{t('fluxo_unico.breadcrumb.record')}</span>
					</p>

					<div className="mb-6 flex flex-wrap items-center gap-3">

						<div className="size-6 rotate-45 rounded-sm border border-[#2f70f0] bg-[#2f70f0] shadow-[0_8px_18px_rgba(47,112,240,0.18)]" />
						<h1 className="text-2xl font-bold leading-tight tracking-normal md:text-3xl">
							Container #{containerPainel}
						</h1>
						<span className="rounded border border-[#ef4444] bg-[#fff1f2] px-4 py-2 font-mono text-xs font-bold text-[#dc2626]">
							● {t('fluxo_unico.status.problem_detected')}
						</span>
					</div>

					<p className="mb-6 text-sm text-[#64748b]">
						{t('fluxo_unico.panel.client')}: {clientePainel} | {t('fluxo_unico.panel.location')}: {localizacaoPainel}, {posicaoPainel} |
						{''} {t('fluxo_unico.panel.model')}: {modeloPainel}
					</p>

					<div className="mb-6 rounded-lg border border-[#fecaca] bg-[#fff1f2] p-6 shadow-sm">
						<div className="mb-6 flex items-center gap-4">
							<div className="text-3xl text-[#ffd166]">⚠</div>
							<div>
								<p className="text-lg font-bold text-[#dc2626]">
									{t('fluxo_unico.status.active_alert')}
								</p>
								<p className="text-xs text-[#64748b]">
									{t('fluxo_unico.panel.detected_at', { date: dataDeteccao })}
								</p>
							</div>
						</div>

						{!supplyReturnAtivado ? (
							<div className="grid gap-6 sm:grid-cols-3">
								<div>
									<p className="text-sm text-[#64748b]">
										{t('fluxo_unico.panel.configured_temperature')}
									</p>
									<p className="mt-2 font-mono font-bold text-[#1f2937]">
										{formatarTemperatura(setPoint)}
									</p>
								</div>
								<div>
									<p className="text-sm text-[#64748b]">
										{t('fluxo_unico.panel.current_temperature')}
									</p>
									<p className="mt-2 font-mono font-bold text-[#dc2626]">
										{formatarTemperatura(temperaturaAtual)} ⚠
									</p>
								</div>
								<div>
									<p className="text-sm text-[#64748b]">
										{t('fluxo_unico.panel.variation')}
									</p>
									<p className="mt-2 font-mono font-bold text-[#dc2626]">
										{formatarTemperatura(variacao)}
									</p>
								</div>
							</div>
						) : (
							<>
								<div className="grid gap-6 sm:grid-cols-3">
									<div>
										<p className="text-sm text-[#64748b]">
											{t('fluxo_unico.panel.configured_return')}
										</p>
										<p className="mt-2 font-mono font-bold text-[#1f2937]">
											{formatarTemperatura(retorno)}
										</p>
									</div>
									<div>
										<p className="text-sm text-[#64748b]">
											{t('fluxo_unico.panel.current_return')}
										</p>
										<p className="mt-2 font-mono font-bold text-[#dc2626]">
											{formatarTemperatura(returnoAtual)} ⚠
										</p>
									</div>
									<div>
										<p className="text-sm text-[#64748b]">
											{t('fluxo_unico.panel.variation')}
										</p>
										<p className="mt-2 font-mono font-bold text-[#dc2626]">
											{formatarTemperatura(variacaoRetorno)}
										</p>
									</div>
								</div>

								<div className="grid gap-6 sm:grid-cols-3 mt-5">
									<div>
										<p className="text-sm text-[#64748b]">
											{t('fluxo_unico.panel.configured_supply')}
										</p>
										<p className="mt-2 font-mono font-bold text-[#1f2937]">
											{formatarTemperatura(supply)}
										</p>
									</div>
									<div>
										<p className="text-sm text-[#64748b]">
											{t('fluxo_unico.panel.current_supply')}
										</p>
										<p className="mt-2 font-mono font-bold text-[#dc2626]">
											{formatarTemperatura(supplyAtual)} ⚠
										</p>
									</div>
									<div>
										<p className="text-sm text-[#64748b]">
											{t('fluxo_unico.panel.variation')}
										</p>
										<p className="mt-2 font-mono font-bold text-[#dc2626]">
											{formatarTemperatura(variacaoSupply)}
										</p>
									</div>
								</div>
							</>
						)}


						<button
							className="mt-6 inline-flex items-center gap-3 rounded-md border border-[#2f70f0] bg-[#2f70f0] px-4 py-3 font-bold text-white transition-colors hover:bg-[#245dcc]"
							onClick={lidarComAberturaOrdemServico}
							type="button"
						>
							{t('fluxo_unico.panel.open_report')}
						</button>
					</div>

					<div className="mb-6 grid gap-6 md:grid-cols-3">
						<div className="rounded-lg border border-[#d9e1ea] bg-white p-6 shadow-sm">
							<p className="font-mono text-sm font-bold uppercase tracking-[0.12em] text-[#64748b]">
								📊 {t('fluxo_unico.panel.operational_summary')}
							</p>

							{!supplyReturnAtivado ? (
								<div className="mt-5 border-t border-[#e2e8f0] pt-5">
									<p className="text-3xl font-bold">
										{formatarTemperatura(setPoint)}
									</p>
									<p className="mt-1 text-xs text-[#64748b]">
										{t('fluxo_unico.form.configured_set_point')}
									</p>
									<p className="mt-5 text-xs text-[#64748b]">
										{t('fluxo_unico.panel.last_check')}
									</p>
									<p className="mt-1 font-mono text-sm text-[#1f2937]">
										{dataDeteccao}
									</p>
								</div>
							) : (
								<div className="mt-5 border-t border-[#e2e8f0] pt-5">
									<div className='flex'>
										<p className="text-3xl font-bold mr-2">
											{formatarTemperatura(retorno)}
										</p>
									<p className="mt-1 text-xs text-[#64748b]">
											{t('fluxo_unico.panel.configured_return')}
										</p>
										<p className="text-3xl font-bold mr-2">
											{formatarTemperatura(supply)}
										</p>
										<p className="mt-1 text-xs text-[#64748b]">
											{t('fluxo_unico.panel.configured_supply')}
										</p>
									</div>
									<p className="mt-5 text-xs text-[#64748b]">
										{t('fluxo_unico.panel.last_check')}
									</p>
									<p className="mt-1 font-mono text-sm text-[#1f2937]">
										{dataDeteccao}
									</p>
								</div>
							)}
						</div>

						<div className="rounded-lg border border-[#d9e1ea] bg-white p-6 shadow-sm">
							<p className="font-mono text-sm font-bold uppercase tracking-[0.12em] text-[#64748b]">
								📋 {t('fluxo_unico.panel.technical_reports')}
							</p>
							<div className="mt-5 flex border-t border-[#e2e8f0] pt-5">
								<div className="mx-3">
									<p className="text-3xl font-bold text-[#dc2626]">
										{quantidadeOrdensAbertas}
									</p>
									<p className="mt-3 text-xs text-[#64748b]">
										{t('fluxo_unico.panel.open')}
									</p>
								</div>
								<div className="mx-3">
									<p className="text-3xl font-bold text-[#1f2937]">
										{quantidadeOrdensResolvidas}
									</p>
									<p className="mt-3 text-xs text-[#64748b]">
										{t('fluxo_unico.panel.resolved')}
									</p>
								</div>
							</div>
						</div>

						<div className="rounded-lg border border-[#d9e1ea] bg-white p-6 shadow-sm">
							<p className="font-mono text-sm font-bold uppercase tracking-[0.12em] text-[#64748b]">
								📅 {t('fluxo_unico.panel.yard_time')}
							</p>
							<div className="mt-5 border-t border-[#e2e8f0] pt-5">
								<p className="text-3xl font-bold">
									{tempoPatio ?? '-'}
									{tempoPatio !== undefined && (
										<span className="mt-3 text-sm text-[#64748b]">
											{' '}d
										</span>
									)}
								</p>

								<p className="mt-5 text-xs text-[#64748b]">
									{t('fluxo_unico.panel.entry_date')}
								</p>
								<p className="mt-1 font-mono text-sm text-[#1f2937]">
									{dataEntrada || '-'}
								</p>
							</div>
						</div>
					</div>

					<div className="rounded-lg border border-[#d9e1ea] bg-white p-6 shadow-sm">
						<p className="font-mono text-sm font-bold uppercase tracking-[0.12em] text-[#64748b]">
							📜 {t('fluxo_unico.panel.technical_reports_history')}
						</p>
						<div className="mt-5 flex flex-col gap-3 border-t border-[#e2e8f0] pt-4">
							{ordensServico.length ? (
								ordensServico.map((ordemServico) => {
									const ordemFechada = ordemServico.status === 'fechada';
									const ordemRascunho = ordemServico.status === 'rascunho';

									return (
										<button
										className="group flex w-full flex-wrap items-center justify-between gap-4 rounded-md border border-[#d9e1ea] bg-[#f8fafc] p-4 text-left transition duration-150 hover:scale-[1.01] hover:border-[#93c5fd] hover:bg-[#eff6ff] hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#bfdbfe]"
											key={ordemServico.id}
											onClick={() =>
												lidarComSelecaoOrdemServico(ordemServico)
											}
											title={t('fluxo_unico.panel.open_os', { id: ordemServico.id })}
											type="button"
										>
											<div className="min-w-0">
											<p className="font-mono text-sm font-bold text-[#2f70f0]">
													{t('fluxo_unico.breadcrumb.rat', { id: ordemServico.id })}
												</p>
											<p className="mt-2 truncate text-xs text-[#64748b]">
													{ordemServico.descricaoServico || ordemServico.observacaoServico || '-'}
												</p>
											</div>
											<div className="flex items-center gap-4">
												<span
													className={`rounded border px-4 py-2 font-mono text-xs font-bold ${ordemFechada
													? 'border-[#16a34a] bg-[#f0fdf4] text-[#15803d]'
													: ordemRascunho
														? 'border-[#f59e0b] bg-[#fffbeb] text-[#b45309]'
														: 'border-[#ef4444] bg-[#fff1f2] text-[#dc2626]'
														}`}
												>
													{ordemFechada ? '✓ ' : ''}
													{formatarStatusOrdemServico(ordemServico.status)}
												</span>
											<span className="font-mono text-xs text-[#64748b]">
													{formatarDataHora(ordemServico.updatedAt)}
												</span>
												<SVGIcon
													className="h-3 w-3 fill-[#64748b] transition-colors group-hover:fill-[#2f70f0]"
													direction="right"
													icon="Arrow"
												/>
											</div>
										</button>
									);
								})
							) : (
								<p className="py-3 text-sm text-[#64748b]">
									{t('fluxo_unico.panel.no_reports')}
								</p>
							)}
						</div>
					</div>
				</section>
			</main>
		);
	}

	return (
		<TelaLoginFluxoUnico
			aoAlterarInput={lidarComInput}
			aoEntrar={lidarComSubmit}
			carregandoLogin={carregandoLogin}
			carregandoProblemaContainer={carregandoProblemaContainer}
			container={container}
			dataDeteccao={dataDeteccao}
			destinatariosEmail={destinatariosEmail}
			erroLogin={erroLogin}
			formatarDataHora={formatarDataHora}
			formatarTemperatura={formatarTemperatura}
			formularioLoginFluxoUnico={formularioLoginFluxoUnico}
			problemaContainer={problemaContainer}
			remetenteEmail={remetenteEmail}
			tituloEmail={tituloEmail}
		/>
	);
};

export default FluxoUnico;
